import { EditOutlined, PlusOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import { Box, IconButton } from "@mui/material";
import { Alert, Divider, Form, Modal, Space, Switch, Tag, Tooltip, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CountryPaymentGateway,
  CountryPaymentGatewayRequest,
  PaymentGateway,
  PaymentGatewayRequest,
} from "../../../@type/billing.types";
import { BillingService } from "../../../api/billingService";
import Card from "../../../components/common/Card";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import InputField from "../../../components/common/fields/InputField";
import SelectField from "../../../components/common/fields/SelectField";
import StatusBadge from "../../../components/common/StatusBadge";
import MuiTable from "../../../components/common/MuiTable";
import { useCountries } from "../../../hooks/useCountries";
import { Column } from "../../../utils/constants";

const unwrap = (response: any) => response?.data?.data ?? response?.data ?? [];

const IMPLEMENTED_PROCESSORS = new Set(["STRIPE", "VNPAY", "SEPAY"]);

const gatewayTypes = ["CARD", "WALLET", "BANK_TRANSFER", "QR", "GATEWAY"].map((type) => ({
  label: type,
  value: type,
}));

const defaultGatewayValues: PaymentGatewayRequest = {
  code: "",
  name: "",
  type: "GATEWAY",
  description: "",
  icon: "",
  checkout_url: "",
  is_active: true,
  is_default: false,
  display_order: 0,
};

const defaultCountryGatewayValues: CountryPaymentGatewayRequest = {
  country_id: undefined as unknown as number,
  payment_gateway_id: undefined as unknown as number,
  is_enabled: true,
  is_primary: false,
  display_order: 0,
};

interface ModalFooterProps {
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}

const ModalFooter = ({ onCancel, onSave, saving }: ModalFooterProps) => {
  const { t } = useTranslation();
  return (
    <Box display="flex" justifyContent="flex-end" gap={1.5}>
      <CancelButton onClick={onCancel}>{t("btn.cancel")}</CancelButton>
      <ActionButton onClick={onSave} loading={saving}>
        {t("billing.gw.save")}
      </ActionButton>
    </Box>
  );
};

const PaymentGatewayConfig = () => {
  const { t } = useTranslation();
  const [gatewayForm] = Form.useForm<PaymentGatewayRequest>();
  const [countryGatewayForm] = Form.useForm<CountryPaymentGatewayRequest>();
  const [messageApi, contextHolder] = message.useMessage();
  const [gateways, setGateways] = useState<PaymentGateway[]>([]);
  const [countryGateways, setCountryGateways] = useState<CountryPaymentGateway[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingGateway, setSavingGateway] = useState(false);
  const [savingCountryGateway, setSavingCountryGateway] = useState(false);
  const [gatewayModalOpen, setGatewayModalOpen] = useState(false);
  const [countryGatewayModalOpen, setCountryGatewayModalOpen] = useState(false);
  const [editingGateway, setEditingGateway] = useState<PaymentGateway | null>(null);
  const [editingCountryGateway, setEditingCountryGateway] = useState<CountryPaymentGateway | null>(null);
  const [{ data: countries }] = useCountries();

  const countryOptions = useMemo(
    () =>
      countries
        .filter((country) => country.active)
        .map((country) => ({
          label: `${country.name} (${country.code})`,
          value: country.id,
        })),
    [countries]
  );

  const gatewayOptions = useMemo(
    () =>
      gateways.map((gateway) => ({
        label: `${gateway.name} (${gateway.code})`,
        value: gateway.id,
      })),
    [gateways]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [gatewayResponse, countryGatewayResponse] = await Promise.all([
        BillingService.getPaymentGateways(),
        BillingService.getCountryPaymentGateways(),
      ]);
      setGateways(unwrap(gatewayResponse));
      setCountryGateways(unwrap(countryGatewayResponse));
    } catch (error) {
      messageApi.error(t("billing.gw.load_error"));
    } finally {
      setLoading(false);
    }
  }, [messageApi, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateGateway = () => {
    setEditingGateway(null);
    gatewayForm.setFieldsValue(defaultGatewayValues);
    setGatewayModalOpen(true);
  };

  const openEditGateway = (gateway: PaymentGateway) => {
    setEditingGateway(gateway);
    gatewayForm.setFieldsValue({
      code: gateway.code,
      name: gateway.name,
      type: gateway.type,
      description: gateway.description || "",
      icon: gateway.icon || "",
      checkout_url: gateway.checkout_url || "",
      is_active: gateway.is_active,
      is_default: gateway.is_default,
      display_order: gateway.display_order,
    });
    setGatewayModalOpen(true);
  };

  const saveGateway = async () => {
    try {
      const values = await gatewayForm.validateFields();
      setSavingGateway(true);
      const payload = {
        ...values,
        code: values.code.toUpperCase(),
        type: values.type.toUpperCase(),
        display_order: Number(values.display_order || 0),
        is_active: Boolean(values.is_active),
        is_default: Boolean(values.is_default),
      };
      if (editingGateway) {
        await BillingService.updatePaymentGateway(editingGateway.id, payload);
      } else {
        await BillingService.createPaymentGateway(payload);
      }
      messageApi.success(t("billing.gw.saved"));
      setGatewayModalOpen(false);
      await loadData();
    } catch (error: any) {
      if (!error?.errorFields) {
        messageApi.error(t("billing.gw.save_error"));
      }
    } finally {
      setSavingGateway(false);
    }
  };

  const openCreateCountryGateway = () => {
    setEditingCountryGateway(null);
    countryGatewayForm.setFieldsValue(defaultCountryGatewayValues);
    setCountryGatewayModalOpen(true);
  };

  const openEditCountryGateway = (mapping: CountryPaymentGateway) => {
    setEditingCountryGateway(mapping);
    countryGatewayForm.setFieldsValue({
      country_id: mapping.country_id,
      payment_gateway_id: mapping.payment_gateway_id,
      is_enabled: mapping.is_enabled,
      is_primary: mapping.is_primary,
      display_order: mapping.display_order,
    });
    setCountryGatewayModalOpen(true);
  };

  const saveCountryGateway = async () => {
    try {
      const values = await countryGatewayForm.validateFields();
      setSavingCountryGateway(true);
      const payload = {
        ...values,
        display_order: Number(values.display_order || 0),
        is_enabled: Boolean(values.is_enabled),
        is_primary: Boolean(values.is_primary),
      };
      if (editingCountryGateway) {
        await BillingService.updateCountryPaymentGateway(editingCountryGateway.id, payload);
      } else {
        await BillingService.createCountryPaymentGateway(payload);
      }
      messageApi.success(t("billing.gw.assign_saved"));
      setCountryGatewayModalOpen(false);
      await loadData();
    } catch (error: any) {
      if (!error?.errorFields) {
        messageApi.error(t("billing.gw.assign_save_error"));
      }
    } finally {
      setSavingCountryGateway(false);
    }
  };

  const gatewayHeader: Column[] = [
    { id: "code", label: t("billing.gw.col.code"), minWidth: 100, align: "left" },
    {
      id: "name",
      label: t("billing.gw.col.name"),
      minWidth: 160,
      align: "left",
      format: (value: string, row: PaymentGateway) => {
        const isSupported = IMPLEMENTED_PROCESSORS.has(row.code.toUpperCase());
        return (
          <Space direction="vertical" size={2}>
            <span className="font-semibold">{value}</span>
            {!isSupported && (
              <Tooltip title={t("billing.gw.no_processor_tip")}>
                <Tag color="warning" icon={<WarningOutlined />} style={{ fontSize: 11, margin: 0 }}>
                  {t("billing.gw.no_processor")}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    { id: "type", label: t("billing.gw.col.type"), minWidth: 120, align: "center" },
    {
      id: "is_active",
      label: t("billing.gw.col.status"),
      minWidth: 100,
      align: "center",
      format: (value: boolean) => (
        <StatusBadge label={value ? t("billing.gw.active") : t("billing.gw.inactive")} tone={value ? "success" : "error"} />
      ),
    },
    {
      id: "is_default",
      label: t("billing.gw.col.default"),
      minWidth: 100,
      align: "center",
      format: (value: boolean) => (value ? <StatusBadge label={t("billing.gw.col.default")} tone="info" /> : "-"),
    },
    { id: "display_order", label: t("billing.gw.col.order"), minWidth: 80, align: "right" },
    {
      id: "id",
      label: t("billing.gw.col.actions"),
      minWidth: 90,
      align: "center",
      format: (_: number, row: PaymentGateway) => (
        <IconButton size="small" color="success" onClick={() => openEditGateway(row)}>
          <EditOutlined />
        </IconButton>
      ),
    },
  ];

  const countryGatewayHeader: Column[] = [
    {
      id: "country_name",
      label: t("billing.gw.col.country"),
      minWidth: 160,
      align: "left",
      format: (_: string, row: CountryPaymentGateway) => (
        <Space direction="vertical" size={0}>
          <span className="font-semibold">{row.country_name}</span>
          <Typography.Text type="secondary">{row.country_code}</Typography.Text>
        </Space>
      ),
    },
    {
      id: "gateway_name",
      label: t("billing.gw.col.gateway"),
      minWidth: 170,
      align: "left",
      format: (_: string, row: CountryPaymentGateway) => {
        const isSupported = IMPLEMENTED_PROCESSORS.has(row.gateway_code.toUpperCase());
        return (
          <Space direction="vertical" size={0}>
            <Space size={4}>
              <span className="font-semibold">{row.gateway_name}</span>
              {!isSupported && (
                <Tooltip title={t("billing.gw.no_processor_tip_short")}>
                  <Tag color="warning" icon={<WarningOutlined />} style={{ fontSize: 10, margin: 0 }}>
                    {t("billing.gw.no_processor")}
                  </Tag>
                </Tooltip>
              )}
            </Space>
            <Typography.Text type="secondary">{row.gateway_code}</Typography.Text>
          </Space>
        );
      },
    },
    { id: "gateway_type", label: t("billing.gw.col.type"), minWidth: 120, align: "center" },
    {
      id: "is_enabled",
      label: t("billing.gw.col.enabled"),
      minWidth: 100,
      align: "center",
      format: (value: boolean) => (
        <StatusBadge label={value ? t("billing.gw.enabled") : t("billing.gw.disabled")} tone={value ? "success" : "error"} />
      ),
    },
    {
      id: "is_primary",
      label: t("billing.gw.col.primary"),
      minWidth: 100,
      align: "center",
      format: (value: boolean) => (value ? <StatusBadge label={t("billing.gw.col.primary")} tone="info" /> : "-"),
    },
    { id: "display_order", label: t("billing.gw.col.order"), minWidth: 80, align: "right" },
    {
      id: "id",
      label: t("billing.gw.col.actions"),
      minWidth: 90,
      align: "center",
      format: (_: number, row: CountryPaymentGateway) => (
        <IconButton size="small" color="primary" onClick={() => openEditCountryGateway(row)}>
          <EditOutlined />
        </IconButton>
      ),
    },
  ];

  return (
    <Card className="user">
      {contextHolder}
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t("billing.gw.title")}
          </Typography.Title>
          <CancelButton startIcon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            {t("billing.gw.reload")}
          </CancelButton>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t("billing.gw.gateways")}
          </Typography.Title>
          <ActionButton startIcon={<PlusOutlined />} onClick={openCreateGateway}>
            {t("billing.gw.add")}
          </ActionButton>
        </Box>
        <MuiTable header={gatewayHeader} data={gateways} isLoading={loading} />

        <Divider style={{ margin: 0 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t("billing.gw.assignments")}
          </Typography.Title>
          <ActionButton startIcon={<PlusOutlined />} onClick={openCreateCountryGateway}>
            {t("billing.gw.assign")}
          </ActionButton>
        </Box>
        <MuiTable header={countryGatewayHeader} data={countryGateways} isLoading={loading} />
      </Space>

      <Modal
        title={t("billing.gw.modal_gateway")}
        open={gatewayModalOpen}
        onCancel={() => setGatewayModalOpen(false)}
        footer={
          <ModalFooter onCancel={() => setGatewayModalOpen(false)} onSave={saveGateway} saving={savingGateway} />
        }
      >
        <Form form={gatewayForm} layout="vertical" initialValues={defaultGatewayValues}>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.code !== cur.code}>
            {() => {
              const code = String(gatewayForm.getFieldValue("code") || "").toUpperCase();
              if (code && !IMPLEMENTED_PROCESSORS.has(code)) {
                return (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t("billing.gw.warn_title")}
                    description={t("billing.gw.warn_gateway_desc", { code })}
                  />
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item name="code" label={t("billing.gw.col.code")} rules={[{ required: true, message: t("billing.gw.code_required") }]}>
            <InputField disabled={Boolean(editingGateway)} />
          </Form.Item>
          <Form.Item name="name" label={t("billing.gw.col.name")} rules={[{ required: true, message: t("billing.gw.name_required") }]}>
            <InputField />
          </Form.Item>
          <Form.Item name="type" label={t("billing.gw.col.type")} rules={[{ required: true, message: t("billing.gw.type_required") }]}>
            <SelectField options={gatewayTypes} searchable={false} matchAnchorWidth />
          </Form.Item>
          <Form.Item name="description" label={t("billing.gw.description")}>
            <InputField />
          </Form.Item>
          <Form.Item name="icon" label={t("billing.gw.icon")}>
            <InputField />
          </Form.Item>
          <Form.Item name="checkout_url" label={t("billing.gw.checkout_url")}>
            <InputField />
          </Form.Item>
          <Form.Item name="display_order" label={t("billing.gw.display_order")}>
            <InputField type="number" inputProps={{ min: 0 }} />
          </Form.Item>
          <Space size={24}>
            <Form.Item name="is_active" label={t("billing.gw.active_label")} valuePropName="checked">
              <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
            </Form.Item>
            <Form.Item name="is_default" label={t("billing.gw.default_fallback")} valuePropName="checked">
              <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={t("billing.gw.modal_assign")}
        open={countryGatewayModalOpen}
        onCancel={() => setCountryGatewayModalOpen(false)}
        footer={
          <ModalFooter
            onCancel={() => setCountryGatewayModalOpen(false)}
            onSave={saveCountryGateway}
            saving={savingCountryGateway}
          />
        }
      >
        <Form form={countryGatewayForm} layout="vertical" initialValues={defaultCountryGatewayValues}>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.payment_gateway_id !== cur.payment_gateway_id}>
            {() => {
              const selectedId = countryGatewayForm.getFieldValue("payment_gateway_id");
              const selectedGateway = gateways.find((g) => g.id === selectedId);
              if (selectedGateway && !IMPLEMENTED_PROCESSORS.has(selectedGateway.code.toUpperCase())) {
                return (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t("billing.gw.warn_title")}
                    description={t("billing.gw.warn_assign_desc", { name: selectedGateway.name, code: selectedGateway.code })}
                  />
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item name="country_id" label={t("billing.gw.col.country")} rules={[{ required: true, message: t("billing.gw.country_required") }]}>
            <SelectField options={countryOptions} />
          </Form.Item>
          <Form.Item
            name="payment_gateway_id"
            label={t("billing.gw.gateway_label")}
            rules={[{ required: true, message: t("billing.gw.gateway_required") }]}
          >
            <SelectField options={gatewayOptions} />
          </Form.Item>
          <Form.Item name="display_order" label={t("billing.gw.display_order")}>
            <InputField type="number" inputProps={{ min: 0 }} />
          </Form.Item>
          <Space size={24}>
            <Form.Item name="is_enabled" label={t("billing.gw.enabled_label")} valuePropName="checked">
              <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
            </Form.Item>
            <Form.Item name="is_primary" label={t("billing.gw.primary_label")} valuePropName="checked">
              <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Card>
  );
};

export default PaymentGatewayConfig;
